class FilazooProductBuyBar {
  constructor(productInfo) {
    this.productInfo = productInfo;
    this.sectionId = productInfo.dataset.section;
    this.form = productInfo.querySelector(`#product-form-${this.sectionId}`);
    this.quantityInput = productInfo.querySelector(`#Quantity-${this.sectionId}`);
    if (this.form && !this.quantityInput) {
      const variantInput = this.form.querySelector('.product-variant-id');
      this.quantityInput = document.createElement('input');
      this.quantityInput.type = 'hidden';
      this.quantityInput.name = 'quantity';
      this.quantityInput.value = variantInput?.dataset.quantityMin || '1';
      this.quantityInput.min = this.quantityInput.value;
      this.quantityInput.step = '1';
      this.form.appendChild(this.quantityInput);
    }
    this.stickyBar = document.getElementById(`FilazooStickyBuyBar-${this.sectionId}`);
    this.total = this.stickyBar?.querySelector('[data-filazoo-buy-total]');
    this.totalLabel = this.stickyBar?.querySelector('[data-filazoo-buy-total-label]');
    this.originalTotal = this.stickyBar?.querySelector('[data-filazoo-buy-original]');
    this.saving = this.stickyBar?.querySelector('[data-filazoo-buy-saving]');
    this.buyNowButton = productInfo.querySelector('[data-filazoo-buy-now]');
    this.stickyAddButton = this.stickyBar?.querySelector('[data-filazoo-sticky-add]');
    this.stickyBuyNowButton = this.stickyBar?.querySelector('[data-filazoo-sticky-buy-now]');
    this.submitButton = productInfo.querySelector(`#ProductSubmitButton-${this.sectionId}`);
    this.priceContainer = productInfo.querySelector(`#price-${this.sectionId}`);
    this.colorQuantityPicker = productInfo.querySelector('product-color-quantity-picker');
    this.colorSelection = this.colorQuantityPicker?.getSelection?.() || [];
    this.colorSelectionLoading = false;
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.variantSheet = productInfo.querySelector('[data-filazoo-variant-sheet]');
    this.variantSheetOptions = this.variantSheet?.querySelector('[data-filazoo-variant-sheet-options]');
    this.variantSheetQuantity = this.variantSheet?.querySelector('[data-filazoo-sheet-quantity]');
    this.variantSheetAdd = this.variantSheet?.querySelector('[data-filazoo-variant-sheet-add]');
    this.variantSheetBuy = this.variantSheet?.querySelector('[data-filazoo-variant-sheet-buy]');
    this.allowDirectProductAction = false;

    if (!this.form) return;

    this.productInfo.addEventListener('change', (event) => {
      if (event.target.matches(`#Quantity-${this.sectionId}`) || event.target.matches('.product-variant-id')) {
        requestAnimationFrame(() => this.updateTotal());
      }
    });

    this.productInfo.addEventListener('input', (event) => {
      if (event.target.matches(`#Quantity-${this.sectionId}`)) {
        this.updateTotal();
        this.syncMobileVariantSheet();
      }
    });

    this.productInfo.addEventListener('color-quantity:change', (event) => {
      this.colorSelection = event.detail?.selection || [];
      this.colorSelectionLoading = Boolean(event.detail?.loading);
      this.updateTotal();
      this.syncAvailability();
    });

    if (this.priceContainer) {
      this.priceObserver = new MutationObserver(() => this.updateTotal());
      this.priceObserver.observe(this.priceContainer, { childList: true, subtree: true });
    }

    if (this.submitButton && this.buyNowButton) {
      this.availabilityObserver = new MutationObserver(() => this.syncAvailability());
      this.availabilityObserver.observe(this.submitButton, {
        attributes: true,
        attributeFilter: ['disabled'],
        childList: true,
        subtree: true,
      });
    }

    this.buyNowButton?.addEventListener('click', () => this.buyNow());
    this.stickyBuyNowButton?.addEventListener('click', () => this.handleStickyBuyNow());
    this.stickyAddButton?.addEventListener('click', () => this.handleStickyAdd());
    this.initializeMobileVariantSheet();
    this.syncAvailability();
    this.updateTotal();
  }

  syncAvailability() {
    if (!this.submitButton) return;

    const hasColorSelection = this.colorSelection.length > 0;
    const disabled = this.colorSelectionLoading || (!hasColorSelection && this.submitButton.disabled);
    if (this.buyNowButton) this.buyNowButton.disabled = disabled;
    if (this.stickyBuyNowButton) this.stickyBuyNowButton.disabled = disabled;
    if (this.stickyAddButton) {
      this.stickyAddButton.disabled = disabled;
      const sourceLabel = this.submitButton.querySelector('span')?.textContent?.trim();
      const destinationLabel = this.stickyAddButton.querySelector('span');
      if (sourceLabel && destinationLabel) destinationLabel.textContent = sourceLabel;
    }
    if (this.variantSheetAdd) this.variantSheetAdd.disabled = disabled;
    if (this.variantSheetBuy) this.variantSheetBuy.disabled = disabled;
  }

  getPriceData() {
    const data = this.productInfo.querySelector('[data-filazoo-price-data]');
    if (!data) return null;

    const priceBreaksSource = data.querySelector('[data-filazoo-price-breaks]')?.textContent || '[]';
    if (this.cachedPriceDataNode === data && this.cachedPriceBreaksSource === priceBreaksSource) {
      return this.cachedPriceData;
    }

    let priceBreaks = [];
    try {
      priceBreaks = JSON.parse(priceBreaksSource);
    } catch (error) {
      priceBreaks = [];
    }

    this.cachedPriceDataNode = data;
    this.cachedPriceBreaksSource = priceBreaksSource;
    this.cachedPriceData = {
      price: Number(data.dataset.price || 0),
      currency: data.dataset.currency || 'USD',
      priceBreaks,
    };
    return this.cachedPriceData;
  }

  updateTotal() {
    if (!this.total) return;

    if (this.colorSelection.length > 0) {
      const selectedQuantity = this.colorSelection.reduce(
        (total, item) => total + Number(item.quantity || 0),
        0
      );
      const selectedSubtotal = this.colorSelection.reduce(
        (total, item) => total + Number(item.price || 0) * Number(item.quantity || 0),
        0
      );
      const priceData = this.getPriceData();
      const currency = this.colorQuantityPicker?.dataset.currency || priceData?.currency || 'USD';
      let selectedTotal = selectedSubtotal;

      if (priceData?.price > 0) {
        const activePriceBreak = [...priceData.priceBreaks]
          .sort((a, b) => Number(a.minimum) - Number(b.minimum))
          .filter((priceBreak) => selectedQuantity >= Number(priceBreak.minimum))
          .pop();
        if (activePriceBreak) {
          selectedTotal = Math.round(selectedSubtotal * (Number(activePriceBreak.price) / priceData.price));
        }
      }

      const savings = Math.max(0, selectedSubtotal - selectedTotal);
      const hasBulkOffer = savings > 0;
      this.total.textContent = this.formatMoney(selectedTotal, currency);
      this.total.classList.toggle('filazoo-buy-bar-total__current--offer', hasBulkOffer);
      if (this.totalLabel) this.totalLabel.hidden = false;
      if (this.originalTotal) {
        this.originalTotal.hidden = !hasBulkOffer;
        this.originalTotal.textContent = this.formatMoney(selectedSubtotal, currency);
      }
      if (this.saving) {
        this.saving.hidden = !hasBulkOffer;
        this.saving.textContent = hasBulkOffer
          ? `Saving ${this.formatMoney(savings, currency)}`
          : '';
      }
      return;
    }

    const priceData = this.getPriceData();
    if (!priceData) return;

    const quantity = Math.max(1, Number(this.quantityInput.value || 1));
    let unitPrice = priceData.price;

    priceData.priceBreaks.forEach((priceBreak) => {
      if (quantity >= Number(priceBreak.minimum)) unitPrice = Number(priceBreak.price);
    });

    const originalTotal = priceData.price * quantity;
    const discountedTotal = unitPrice * quantity;
    const savings = Math.max(0, originalTotal - discountedTotal);
    const hasBulkOffer = savings > 0;

    this.total.textContent = this.formatMoney(discountedTotal, priceData.currency);
    this.total.classList.toggle('filazoo-buy-bar-total__current--offer', hasBulkOffer);

    if (this.totalLabel) this.totalLabel.hidden = false;

    if (this.originalTotal) {
      this.originalTotal.hidden = !hasBulkOffer;
      this.originalTotal.textContent = this.formatMoney(originalTotal, priceData.currency);
    }

    if (this.saving) {
      this.saving.hidden = !hasBulkOffer;
      this.saving.textContent = hasBulkOffer
        ? `Saving ${this.formatMoney(savings, priceData.currency)}`
        : '';
    }
  }

  formatMoney(cents, currency) {
    const amount = cents / 100;
    const formatted = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);

    const showCode = this.colorQuantityPicker?.dataset.showCurrencyCode !== 'false';
    return showCode ? `${formatted} ${currency}` : formatted;
  }

  handleStickyAdd() {
    if (this.colorSelection.length > 0) {
      this.colorQuantityPicker?.submit(false);
      return;
    }
    if (this.shouldUseMobileVariantSheet()) {
      this.openMobileVariantSheet();
      return;
    }
    this.submitButton?.click();
  }

  handleStickyBuyNow() {
    if (this.colorSelection.length > 0) {
      this.colorQuantityPicker?.submit(true);
      return;
    }
    if (this.shouldUseMobileVariantSheet()) {
      this.openMobileVariantSheet();
      return;
    }
    this.buyNow();
  }

  get variantSelectors() {
    return this.productInfo.querySelector('variant-selects');
  }

  shouldUseMobileVariantSheet() {
    return this.mobileQuery.matches && Boolean(this.variantSheet && this.variantSelectors);
  }

  initializeMobileVariantSheet() {
    if (!this.variantSheet || !this.variantSelectors || !this.variantSheetOptions) return;

    this.variantSelectorPlaceholder = document.createComment('filazoo-mobile-variant-selector');
    this.variantSelectors.parentNode.insertBefore(this.variantSelectorPlaceholder, this.variantSelectors);
    this.syncMobileVariantSheetPlacement();

    this.mobileQuery.addEventListener('change', () => this.syncMobileVariantSheetPlacement());
    this.productInfo.querySelector('media-gallery')?.addEventListener('click', (event) => {
      const thumbnail = event.target.closest('.thumbnail-list__item[data-filazoo-gallery-color] .thumbnail');
      if (!thumbnail) return;
      this.selectGalleryColor(thumbnail.closest('[data-filazoo-gallery-color]'));
    });
    this.productInfo.querySelector(`#GalleryViewer-${this.sectionId}`)?.addEventListener('slideChanged', (event) => {
      this.selectGalleryColor(event.detail?.currentElement);
    });
    this.productInfo.querySelectorAll('[data-filazoo-variant-sheet-open]').forEach((trigger) => {
      trigger.addEventListener('click', () => this.openMobileVariantSheet());
    });
    this.variantSheet.querySelectorAll('[data-filazoo-variant-sheet-close]').forEach((button) => {
      button.addEventListener('click', () => this.closeMobileVariantSheet());
    });

    this.productInfo.addEventListener(
      'click',
      (event) => {
        if (this.allowDirectProductAction || !this.mobileQuery.matches) return;
        const productAction = event.target.closest(
          `#ProductSubmitButton-${this.sectionId}, [data-filazoo-buy-now]`
        );
        if (!productAction) return;

        if (this.colorSelection.length > 0) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.colorQuantityPicker?.submit(productAction.matches('[data-filazoo-buy-now]'));
          return;
        }

        if (!this.shouldUseMobileVariantSheet()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.openMobileVariantSheet();
      },
      true
    );

    this.variantSheet.querySelector('[data-filazoo-sheet-quantity-minus]')?.addEventListener('click', () => {
      this.adjustSheetQuantity(-1);
    });
    this.variantSheet.querySelector('[data-filazoo-sheet-quantity-plus]')?.addEventListener('click', () => {
      this.adjustSheetQuantity(1);
    });
    this.variantSheetQuantity?.addEventListener('input', () => this.applySheetQuantity(false));
    this.variantSheetQuantity?.addEventListener('change', () => this.applySheetQuantity(true));

    this.variantSheetAdd?.addEventListener('click', () => {
      if (this.variantSheetAdd.disabled) return;
      this.applySheetQuantity(true);
      this.allowDirectProductAction = true;
      this.closeMobileVariantSheet(false);
      this.submitButton?.click();
      queueMicrotask(() => {
        this.allowDirectProductAction = false;
      });
    });
    this.variantSheetBuy?.addEventListener('click', () => {
      if (this.variantSheetBuy.disabled) return;
      this.applySheetQuantity(true);
      this.closeMobileVariantSheet(false);
      this.buyNow();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.variantSheet.classList.contains('is-open')) {
        this.closeMobileVariantSheet();
      }
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.mobileVariantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
        if (String(data?.sectionId || '') !== String(this.sectionId)) return;
        requestAnimationFrame(() => {
          this.syncMobileVariantSheetPlacement();
          this.syncMobileVariantSheet();
          this.syncAvailability();
        });
      });
    }

    this.syncMobileVariantSheet();
  }

  selectGalleryColor(mediaElement) {
    const color = mediaElement?.dataset?.filazooGalleryColor;
    if (!color) return;

    const colorInput = Array.from(
      this.variantSelectors?.querySelectorAll('input[type="radio"][data-option-name]') || []
    ).find((input) => {
      const optionName = String(input.dataset.optionName || '').trim().toLowerCase();
      return (optionName === 'color' || optionName === 'colour') && input.value === color;
    });

    if (!colorInput || colorInput.checked) return;
    colorInput.click();
  }

  syncMobileVariantSheetPlacement() {
    const selector = this.variantSelectors;
    if (!selector || !this.variantSelectorPlaceholder?.parentNode) return;

    if (this.mobileQuery.matches) {
      if (selector.parentNode !== this.variantSheetOptions) this.variantSheetOptions.appendChild(selector);
      return;
    }

    this.closeMobileVariantSheet(false);
    if (selector.previousSibling !== this.variantSelectorPlaceholder) {
      this.variantSelectorPlaceholder.parentNode.insertBefore(selector, this.variantSelectorPlaceholder.nextSibling);
    }
  }

  openMobileVariantSheet() {
    if (!this.shouldUseMobileVariantSheet()) return;
    window.clearTimeout(this.variantSheetCloseTimer);
    this.lastFocusedElement = document.activeElement;
    this.syncMobileVariantSheetPlacement();
    this.syncMobileVariantSheet();
    this.variantSheet.hidden = false;
    this.variantSheet.setAttribute('aria-hidden', 'false');
    document.body.classList.add('filazoo-variant-sheet-open');
    requestAnimationFrame(() => {
      this.variantSheet.classList.add('is-open');
      this.variantSheet.querySelector('.filazoo-variant-sheet__dialog')?.focus({ preventScroll: true });
    });
  }

  closeMobileVariantSheet(restoreFocus = true) {
    if (!this.variantSheet) return;
    this.variantSheet.classList.remove('is-open');
    this.variantSheet.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('filazoo-variant-sheet-open');
    window.clearTimeout(this.variantSheetCloseTimer);
    this.variantSheetCloseTimer = window.setTimeout(() => {
      if (!this.variantSheet.classList.contains('is-open')) this.variantSheet.hidden = true;
    }, 280);
    if (restoreFocus && this.lastFocusedElement?.isConnected) {
      this.lastFocusedElement.focus({ preventScroll: true });
    }
  }

  normalizeSheetQuantity(value) {
    const minimum = Number(this.quantityInput.min || this.quantityInput.dataset.min || 1);
    const maximum = this.quantityInput.max === '' ? Infinity : Number(this.quantityInput.max);
    const step = Math.max(1, Number(this.quantityInput.step || 1));
    const requested = Math.max(minimum, Number(value) || minimum);
    const stepped = minimum + Math.round((requested - minimum) / step) * step;
    return Math.min(maximum, stepped);
  }

  adjustSheetQuantity(direction) {
    if (!this.variantSheetQuantity) return;
    const step = Math.max(1, Number(this.quantityInput.step || 1));
    this.variantSheetQuantity.value = String(
      this.normalizeSheetQuantity(Number(this.variantSheetQuantity.value || 1) + direction * step)
    );
    this.applySheetQuantity(true);
  }

  applySheetQuantity(commit) {
    if (!this.variantSheetQuantity) return;
    const quantity = this.normalizeSheetQuantity(this.variantSheetQuantity.value);
    this.variantSheetQuantity.value = String(quantity);
    this.quantityInput.value = String(quantity);
    this.quantityInput.dispatchEvent(new Event(commit ? 'change' : 'input', { bubbles: true }));
    this.syncMobileVariantSheet();
  }

  getSelectedVariant() {
    try {
      return JSON.parse(this.variantSelectors?.querySelector('[data-selected-variant]')?.textContent || 'null');
    } catch (error) {
      return null;
    }
  }

  syncMobileVariantSheet() {
    if (!this.variantSheet) return;
    const variant = this.getSelectedVariant();
    const quantity = this.normalizeSheetQuantity(this.quantityInput.value);
    const sheetQuantity = this.variantSheetQuantity;
    const image = this.variantSheet.querySelector('[data-filazoo-variant-sheet-image]');
    const price = this.variantSheet.querySelector('[data-filazoo-variant-sheet-price]');
    const total = this.variantSheet.querySelector('[data-filazoo-variant-sheet-total]');
    const priceData = this.getPriceData();
    const currency = priceData?.currency || 'USD';
    const basePrice = Number(variant?.price || priceData?.price || 0);
    let unitPrice = basePrice;

    priceData?.priceBreaks?.forEach((priceBreak) => {
      if (quantity >= Number(priceBreak.minimum)) unitPrice = Number(priceBreak.price);
    });

    if (sheetQuantity) {
      sheetQuantity.value = String(quantity);
      sheetQuantity.min = this.quantityInput.min || this.quantityInput.dataset.min || '1';
      sheetQuantity.step = this.quantityInput.step || '1';
      if (this.quantityInput.max !== '') sheetQuantity.max = this.quantityInput.max;
      else sheetQuantity.removeAttribute('max');
    }
    if (price) price.textContent = this.formatMoney(basePrice, currency);
    if (total) total.textContent = this.formatMoney(unitPrice * quantity, currency);

    const imageSource = variant?.featured_image?.src || variant?.featured_media?.preview_image?.src;
    if (image && imageSource) {
      image.src = imageSource.startsWith('//') ? `${window.location.protocol}${imageSource}` : imageSource;
      image.alt = variant?.title || image.alt;
    }
  }

  async buyNow() {
    if (this.submitButton?.disabled) return;

    const variantInput = this.form.querySelector('.product-variant-id');
    if (!variantInput?.value) return;

    if (this.buyNowButton) this.buyNowButton.disabled = true;
    if (this.stickyBuyNowButton) this.stickyBuyNowButton.disabled = true;
    try {
      const formData = new FormData(this.form);
      formData.set('quantity', String(Math.max(1, Number(this.quantityInput.value || 1))));
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      if (!response.ok) throw new Error('Unable to add this item to checkout.');
      const root = window.Shopify?.routes?.root || '/';
      window.location.assign(`${root}checkout`);
    } catch (error) {
      this.syncAvailability();
      window.alert(error.message);
    }
  }
}

const initializeFilazooBuyBars = (root = document) => {
  root.querySelectorAll('product-info.filazoo-main-product').forEach((productInfo) => {
    if (productInfo.dataset.filazooBuyBarInitialized === 'true') return;
    productInfo.dataset.filazooBuyBarInitialized = 'true';
    new FilazooProductBuyBar(productInfo);
  });
};

const initializeFilazooMobileControls = (root = document) => {
  root.querySelectorAll('[data-filazoo-mobile-controls]').forEach((controls) => {
    if (controls.dataset.initialized === 'true') return;
    controls.dataset.initialized = 'true';

    const backButton = controls.querySelector('[data-filazoo-mobile-back]');
    const menuButton = controls.querySelector('[data-filazoo-mobile-menu]');
    const searchButton = controls.querySelector('[data-filazoo-mobile-search]');
    const cartButton = controls.querySelector('[data-filazoo-mobile-cart]');

    backButton?.addEventListener('click', () => {
      const homeUrl = backButton.dataset.homeUrl || window.Shopify?.routes?.root || '/';
      let referrerUrl;

      try {
        referrerUrl = document.referrer ? new URL(document.referrer) : null;
      } catch (_error) {
        referrerUrl = null;
      }

      // Only go back when the shopper arrived from another page in this store.
      // Browser history can include Google (or another external site), even when
      // this product page is the first page visited in the storefront.
      if (referrerUrl?.origin === window.location.origin && window.history.length > 1) {
        window.history.back();
        return;
      }
      window.location.assign(homeUrl);
    });

    menuButton?.addEventListener('click', () => {
      const mobileShopSheet = document.querySelector('[data-mobile-shop-sheet]');
      if (!mobileShopSheet) {
        const rootUrl = window.Shopify?.routes?.root || '/';
        window.location.href = `${rootUrl.replace(/\/?$/, '/')}collections`;
        return;
      }

      document.dispatchEvent(new CustomEvent('filazoo:open-mobile-shop', { detail: { trigger: menuButton } }));
    });

    searchButton?.addEventListener('click', (event) => {
      const mobileSearchButton = document.querySelector('[data-mobile-search]');
      if (!mobileSearchButton) return;

      event.preventDefault();
      document.querySelector('.bambu-search')?.addEventListener(
        'global-bottom-sheet:closed',
        () => searchButton.focus({ preventScroll: true }),
        { once: true },
      );
      mobileSearchButton.click();
    });

    cartButton?.addEventListener('click', (event) => {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer || typeof cartDrawer.open !== 'function') return;

      event.preventDefault();
      cartDrawer.open(cartButton);
    });
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initializeFilazooBuyBars();
    initializeFilazooMobileControls();
  });
} else {
  initializeFilazooBuyBars();
  initializeFilazooMobileControls();
}

document.addEventListener('shopify:section:load', (event) => {
  initializeFilazooBuyBars(event.target);
  initializeFilazooMobileControls(event.target);
});
